import type { DateRange } from "react-day-picker"

const MONTH_FORMAT = new Intl.DateTimeFormat("en-US", {
  month: "short",
  year: "numeric",
})

const SHORT_MONTH_FORMAT = new Intl.DateTimeFormat("en-US", { month: "short" })

export const MONTH_OPTIONS = Array.from({ length: 12 }, (_, month) => ({
  value: month,
  label: SHORT_MONTH_FORMAT.format(new Date(2020, month, 1)),
}))

export function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1)
}

export function endOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0)
}

export function listYears(start: Date, end: Date): number[] {
  const years: number[] = []
  for (let year = start.getFullYear(); year <= end.getFullYear(); year += 1) {
    years.push(year)
  }
  return years
}

export function monthYearSortKey(month: number, year: number): number {
  return year * 12 + month
}

export function monthYearFromKey(key: number): { month: number; year: number } {
  const month = ((key % 12) + 12) % 12
  return { month, year: (key - month) / 12 }
}

/** Whole-month range spanning the two grid cells (order-independent). */
export function monthRangeFromKeys(aKey: number, bKey: number): DateRange {
  const lo = Math.min(aKey, bKey)
  const hi = Math.max(aKey, bKey)
  const start = monthYearFromKey(lo)
  const end = monthYearFromKey(hi)
  return {
    from: startOfMonth(new Date(start.year, start.month, 1)),
    to: endOfMonth(new Date(end.year, end.month, 1)),
  }
}

export function isMonthRangeOrderInvalid(
  fromMonth: number,
  fromYear: number,
  toMonth: number,
  toYear: number,
): boolean {
  return monthYearSortKey(toMonth, toYear) < monthYearSortKey(fromMonth, fromYear)
}

export type MonthYearBounds = {
  minKey?: number
  maxKey?: number
}

export function getMonthYearBounds(bounds?: {
  fromDate?: Date
  toDate?: Date
}): MonthYearBounds {
  return {
    minKey: bounds?.fromDate
      ? monthYearSortKey(bounds.fromDate.getMonth(), bounds.fromDate.getFullYear())
      : undefined,
    maxKey: bounds?.toDate
      ? monthYearSortKey(bounds.toDate.getMonth(), bounds.toDate.getFullYear())
      : undefined,
  }
}

export function isMonthYearOutOfBounds(
  month: number,
  year: number,
  { minKey, maxKey }: MonthYearBounds,
): boolean {
  const key = monthYearSortKey(month, year)
  if (minKey !== undefined && key < minKey) return true
  if (maxKey !== undefined && key > maxKey) return true
  return false
}

/** Whole calendar months: first day of start month through last day of end month. */
export function buildMonthRangeFromParts(
  fromMonth: number,
  fromYear: number,
  toMonth: number,
  toYear: number,
): DateRange {
  return {
    from: startOfMonth(new Date(fromYear, fromMonth, 1)),
    to: endOfMonth(new Date(toYear, toMonth, 1)),
  }
}

export function snapRangeToMonths(range: DateRange | undefined): DateRange | undefined {
  if (!range?.from) return undefined

  const orderedFrom = range.to && range.from.getTime() > range.to.getTime() ? range.to : range.from
  const orderedTo = range.to && range.from.getTime() > range.to.getTime() ? range.from : (range.to ?? range.from)

  return {
    from: startOfMonth(orderedFrom),
    to: endOfMonth(orderedTo),
  }
}

export function formatMonth(date: Date): string {
  return MONTH_FORMAT.format(date)
}

export function formatMonthRangeLabel(range: DateRange | undefined): string | undefined {
  if (!range?.from) return undefined
  if (!range.to) return `${formatMonth(range.from)} – …`
  const fromLabel = formatMonth(range.from)
  const toLabel = formatMonth(range.to)
  return fromLabel === toLabel ? fromLabel : `${fromLabel} – ${toLabel}`
}

export function countMonths(range: DateRange | undefined): number {
  if (!range?.from || !range.to) return 0
  return (
    (range.to.getFullYear() - range.from.getFullYear()) * 12 +
    (range.to.getMonth() - range.from.getMonth()) +
    1
  )
}
